/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useMemo, useRef } from 'react';

/**
 * A simple no-op function
 * @returns void
 */
export const noop = (): void => {};

/**
 * Converts a string to Pascal case
 * @param string - string
 * @example toPascalCase('hello world') => 'HelloWorld'
 * @returns string
 */
export const toPascalCase = (string: string): string =>
  string
    .split(/[\W_]+/)
    .filter(Boolean)
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join('');

/**
 * Returns the getId function for a label
 * @example getGetIdFn('TextInput')('age') -> 'TextInput-age'
 * @example getGetIdFn('SearchInput')(undefined) -> 'SearchInput'
 * @param componentName - string
 * @returns (label?: string) => string
 */
export const getGetIdFn =
  (componentName: string) =>
  (label?: string): string =>
    label ? `${componentName}-${toPascalCase(label)}` : componentName;

/**
 * Gets CSS class names
 * @param object - Object where key is class name and value is truthy if class should be shown
 * @spreadParam (optional) strings - Class names to be appended
 * @example getClassNames({'a__1': 1, b: 0, c: 'c'}, 'd', 'e') => 'a__1 c d e'
 * @returns string
 */
export const getClassNames = (
  object: Record<string, unknown>,
  ...args: string[]
): string =>
  Object.entries(object)
    .reduce((acc, [key, val]) => (val ? acc.concat(key) : acc), args)
    .join(' ');

// regex for valid integer amounts
export const regexForIntegers = /^[0-9]*$/;
// regex for valid fractional amounts up to two places, i.e. "55", "55.", "55.5", "55.55"
export const regexForFractionsTwoPlaces = /^[0-9]*\.?\d?\d?$/;
// regex for valid fractional amounts, i.e. "55", "55.", "55.5", "55.55555"
export const regexForFractions = /^\d*\.?\d*$/;
// regex for valid fractional amounts that need a 0 padded on the end, i.e. "55.5"
export const regexForFractionWithPadNeeded = /^[0-9]*\.\d$/;
// regex for valid fractional amounts that have a 0 padded on the end, i.e. "55.50"
export const regexForFractionWithPad = /^[0-9]*\.\d0$/;
// regex for valid fractional amounts that need removal of penultimate 0, i.e. "55.505" -> "55.55"
export const regexForFractionWithPadToOmit = /^[0-9]*\.\d0\d$/;

function setRef<T>(
  ref:
    | React.MutableRefObject<T | null>
    | ((instance: T | null) => void)
    | null
    | undefined,
  value: T | null
): void {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ref.current = value;
  }
}

/*
 * A hook that can combine two refs(mutable or callbackRefs) into a single callbackRef
 */
export function useForkRef<InstanceA, InstanceB>(
  refA: React.Ref<InstanceA> | null | undefined,
  refB: React.Ref<InstanceB> | null | undefined
): React.Ref<InstanceA & InstanceB> | null {
  return useMemo(() => {
    if (refA == null && refB == null) {
      return null;
    }
    return (refValue) => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
}

/*
 * We create a ref using useRef and then we combine it with one that is get passed as a parameter
 */
export function useDefaultRef<T>(
  ref: React.Ref<T> | null | undefined
): React.Ref<T> | null {
  const defaultRef = useRef(null);
  return useForkRef(ref, defaultRef);
}

export const hex2rgba = (hex: string, alpha = 1): string => {
  const [r, g, b] = (hex.match(/\w\w/g) || ['0', '0', '0']).map((x) =>
    parseInt(x, 16)
  );
  return `rgba(${r},${g},${b},${alpha})`;
};

export function isInternetExplorer(): boolean {
  return /trident|msie/i.test(window.navigator.userAgent);
}

export const useInputComponentIdAndLabelData = (
  componentName: string,
  label: string | undefined,
  ariaLabel: string | undefined
): {
  id: string;
  containerId: string;
  labelId: string | undefined;
  errorId: string | undefined;
  hintId: string | undefined;
  'aria-labelledby': string | undefined;
} => {
  const id = useMemo(
    () => getGetIdFn(componentName)(label),
    [componentName, label]
  );
  const containerId = useMemo(
    () => getGetIdFn(`${componentName}-Container`)(label),
    [componentName, label]
  );
  const labelId = useMemo(
    () => (label ? getGetIdFn(`${componentName}-Label`)(label) : undefined),
    [componentName, label]
  );
  const errorId = useMemo(
    () => getGetIdFn(`${componentName}-Error`)(label),
    [componentName, label]
  );
  const hintId = useMemo(
    () => getGetIdFn(`${componentName}-Hint`)(label),
    [componentName, label]
  );
  return {
    id,
    containerId,
    labelId,
    // only set aria-labelledby if no aria-label "override" has been explicitly passed
    'aria-labelledby': ariaLabel ? undefined : labelId,
    errorId,
    hintId,
  };
};
